import { createFileRoute } from '@tanstack/react-router'
import { AlternativesPage, alternativesHead, type AlternativesData } from '@/components/alternatives-page'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

const data: AlternativesData = {
  tool: "Better Stack",
  slug: "betterstack",
  intro: "Better Stack combines uptime monitoring, on-call, and logs, and prices uptime per responder. If you want flat pricing or only need uptime checks, these alternatives are worth a look.",
  alternatives: [
    {
      name: "Uptrack",
      description: "Uptime monitoring with 30-second checks, alert confirmation, browser checks, and status pages. A focused option if you only use Better Stack for uptime monitoring.",
      pricing: "Free (5 monitors), Pro $3/mo, Team $20/mo, Business $79/mo",
      pros: ["30-second checks on every plan", "Alerts only after 2-5 consecutive failed checks", "Flat pricing with a 30-day money-back guarantee"],
      cons: ["Free plan is 5 monitors at 30 s from one location", MONITOR_LOCATIONS.alternativesCon, "No APM or logs"],
      url: "https://uptrack.app",
      isUptrack: true,
    },
    {
      name: "UptimeRobot",
      description: "A widely used uptime monitor with a free plan of 50 monitors at 5-minute checks, now allowed for commercial use. 30-second checks start on the Team plan.",
      pricing: "Free (50 monitors), Solo $10/mo, Team $41/mo",
      pros: ["Free plan with 50 monitors, commercial use allowed", "Mobile apps for iOS and Android", "15-second checks on the Scale plan"],
      cons: ["Free plan checks every 5 minutes; Solo every 60 seconds", "30-second checks start at $41/mo (Team)"],
      url: "https://uptimerobot.com",
    },
    {
      name: "Pulsetic",
      description: "Status pages and uptime monitoring, with status page custom domains on every plan.",
      pricing: "Free (10 monitors), paid plans from Solo",
      pros: ["Status page custom domains on all plans, including free", "Up to 15 check regions on Team", "SMS, call, and WhatsApp alerts billed per message"],
      cons: ["Free plan checks every 5 minutes", "30-second checks start on the Team plan", "Teammates cost $8/mo each"],
      url: "https://pulsetic.com",
    },
    {
      name: "Oh Dear",
      description: "Site health monitoring: uptime, SSL, DNS, broken links, mixed content, Lighthouse, cron, and status pages.",
      pricing: "From $17/mo (2 sites), 10-day trial",
      pros: ["Broken link and mixed content checks", "Application health checks for Laravel/PHP apps", "Unlimited users on all plans"],
      cons: ["No free plan", "1-minute check interval"],
      url: "https://ohdear.app",
    },
    {
      name: "HetrixTools",
      description: "Uptime, blacklist, and server monitoring, with a free plan of 15 monitors at 1-minute checks.",
      pricing: "Free (15 monitors), Professional $9.95/mo",
      pros: ["Free plan with 15 monitors at 1-minute checks", "Blacklist and agent-based server monitoring", "Unlimited status pages on all plans"],
      cons: ["1-minute minimum interval on all plans", "Professional plan covers 30 monitors"],
      url: "https://hetrixtools.com",
    },
  ],
  comparisonTable: [
    { tool: "Uptrack", freePlan: "5 monitors at 30s", checkInterval: "30s (all plans)", alertConfirmation: "Yes (2-5 consecutive)", startingPrice: "Free; Pro $3/mo" },
    { tool: "UptimeRobot", freePlan: "50 monitors", checkInterval: "5min (free), 30s (Team)", alertConfirmation: "Multi-location for timeouts", startingPrice: "$10/mo" },
    { tool: "Pulsetic", freePlan: "10 monitors", checkInterval: "5min (free), 30s (Team)", alertConfirmation: "Not documented", startingPrice: "Solo plan (see site)" },
    { tool: "Oh Dear", freePlan: "None", checkInterval: "1min", alertConfirmation: "Yes (second location)", startingPrice: "$17/mo" },
    { tool: "HetrixTools", freePlan: "15 monitors", checkInterval: "1min", alertConfirmation: "Yes (multi-location)", startingPrice: "$9.95/mo" },
  ],
}

export const Route = createFileRoute('/_site/alternatives/betterstack')({
  component: () => <AlternativesPage data={data} />,
  head: () => alternativesHead(data),
})
